import getUID from '@jetbrains/ring-ui/components/global/get-uid'
import RingHeading from '@jetbrains/ring-ui/components/heading/heading'
import classNames from 'classnames'
import * as React from 'react'

import {closestUpTo, contains} from '../../../utils/dom'
import type {KeyValue} from '../../../utils/object'

import styles from './Details.css'

const getAllHeaders = () => document.querySelectorAll<HTMLElement>('[data-accordion-header]')

type ImitatedHeadingProps = React.JSX.IntrinsicElements['div'] & {
  level: number
}
function ImitatedHeading({level, ...restProps}: ImitatedHeadingProps) {
  return <div role="heading" aria-level={level} {...restProps} />
}

export type Props = {
  open?: boolean
  onToggle?: (open: boolean) => unknown
  level?: number
  renderButtonAside?: boolean
  summary: React.ReactNode
  children?: React.ReactNode
  label: string
  imitateHeading?: boolean
  summaryClassName?: string
  headingClassName?: string
  detailsWrapperClassName?: string
  detailsClassName?: string
}

const DEFAULT_LEVEL = 3
class Details extends React.Component<Props> {
  button: HTMLElement | null = null
  id: string = getUID('details-')
  buttonRef = (el: HTMLElement | null) => {
    this.button = el
  }

  handleClick: (event: React.MouseEvent) => void = e => {
    if (!this.checkMouseEvent(e)) {
      return
    }

    this.toggle()
  }

  handleMouseDown = (e: React.MouseEvent) => {
    if (this.checkMouseEvent(e)) {
      e.preventDefault()
      this.button?.focus()
    }
  }

  handleKeyDown: (event: React.KeyboardEvent) => void = e => {
    // Block events from popups
    if (!contains(e.currentTarget, e.target)) {
      return
    }

    const button = this.button
    const triggeredOnButton = e.target === button

    const headers = [...getAllHeaders()]
    const currentIndex = button != null ? headers.indexOf(button) : -1
    const indexMap: KeyValue<string, number> = {
      Home: 0,
      ArrowUp: triggeredOnButton ? currentIndex - 1 : currentIndex,
      ArrowDown: currentIndex + 1,
      End: headers.length - 1,
    }
    const indexToFocus = indexMap[e.key]

    if (
      indexToFocus != null &&
      (indexToFocus !== currentIndex || !triggeredOnButton) &&
      headers[indexToFocus] != null
    ) {
      e.preventDefault()
      e.stopPropagation()
      headers[indexToFocus].focus()
    }
  }

  checkMouseEvent(e: React.MouseEvent) {
    if (!(e.target instanceof Node) || !e.currentTarget.contains(e.target)) {
      return false
    }

    const interactiveTarget = closestUpTo(e.target, e.currentTarget, 'a,button,input')

    return !(interactiveTarget != null && interactiveTarget !== this.button)
  }

  toggle() {
    const {onToggle, open} = this.props
    onToggle?.(!open)
  }

  render(): React.ReactNode {
    const {
      open,
      summary,
      children,
      level = DEFAULT_LEVEL,
      label,
      renderButtonAside,
      imitateHeading,
      summaryClassName,
      headingClassName,
      detailsWrapperClassName,
      detailsClassName,
    } = this.props
    const summaryClasses = classNames(styles.button, summaryClassName, {
      [styles.asideButton]: renderButtonAside,
    })
    const Heading = imitateHeading === true ? ImitatedHeading : RingHeading
    return (
      <>
        <Heading
          level={level}
          className={classNames(styles.heading, headingClassName)}
          onClick={this.handleClick}
          onMouseDown={this.handleMouseDown}
          onKeyDown={this.handleKeyDown}
        >
          <button
            ref={this.buttonRef}
            type="button"
            className={summaryClasses}
            data-accordion-header
            aria-expanded={open}
            aria-controls={this.id}
            aria-label={label}
          >
            {!renderButtonAside && summary}
          </button>
          {renderButtonAside && summary}
        </Heading>
        <div className={detailsWrapperClassName}>
          <div // Listens to children keydown events
            role="presentation"
            data-accordion-content
            id={this.id}
            className={detailsClassName}
            hidden={!open}
            onKeyDown={this.handleKeyDown}
          >
            {open && children}
          </div>
        </div>
      </>
    )
  }
}

export default Details
